import { IDomainWideProjectRole } from "@kuika/kuika-cl-model";
import { AxiosResponse } from "axios";
import * as React from "react";
import { ChangeEvent } from "react";
import { DomainService } from "../../../../services/domain-service";
import { IWithHistoryProp, withHistory } from "../../../../shared/hoc/with-history";
import { withManagement } from "../../../../shared/hoc/with-management";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import KuikaBadge from "../../../ui-kit/kuika-badge/kuika-badge";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import KuikaDropdownMenu from "../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaRoleBadge from "../../../ui-kit/kuika-role-badge/kuika-role-badge";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTable, { KuikaTableColumn } from "../../../ui-kit/kuika-table/kuika-table";
import KuikaH4 from "../../../ui-kit/typography/H4/H4";
import { ManageRoleProjectsModal } from "./manage-role-projects-modal";
import { RenameRoleModal } from "./rename-role-modal";
import { aggregateWorkspaceRoles, IWorkspaceRole } from "./role-aggregation";
import "../settings-panel.scss";
import "./roles.scss";

declare let window: any;

interface IRolesState {
  roleList: IWorkspaceRole[];
  filteredRoleList: IWorkspaceRole[];
  searchText: string;
  isDataLoading: boolean;
  manageRole: IWorkspaceRole | null;
  isManageModalOpen: boolean;
  renameRole: IWorkspaceRole | null;
  isRenameModalOpen: boolean;
}

// Workspace app-role registry (KOA-18634). Shows the domain's app roles aggregated across every
// project (the truth the platform already enforces via name-keyed matching) and lets the user
// extend a role into other projects. Duplicate ids for the same name are reconciled automatically
// at code generation / deploy, so they are not surfaced here.
class RolesBase extends React.PureComponent<IWithHistoryProp, IRolesState> {
  constructor(props: IWithHistoryProp) {
    super(props);
    this.state = {
      roleList: [],
      filteredRoleList: [],
      searchText: "",
      isDataLoading: false,
      manageRole: null,
      isManageModalOpen: false,
      renameRole: null,
      isRenameModalOpen: false
    };
  }

  openManageModal = (role: IWorkspaceRole): void => {
    this.setState({ manageRole: role, isManageModalOpen: true });
  };

  closeManageModal = (): void => {
    this.setState({ isManageModalOpen: false });
  };

  openRenameModal = (role: IWorkspaceRole): void => {
    this.setState({ renameRole: role, isRenameModalOpen: true });
  };

  closeRenameModal = (): void => {
    this.setState({ isRenameModalOpen: false });
  };

  async componentDidMount(): Promise<void> {
    await this.loadRoles();
  }

  loadRoles = async (): Promise<void> => {
    this.setState({ isDataLoading: true });
    try {
      const response: AxiosResponse<IDomainWideProjectRole[]> = await DomainService.GetDomainWideProjectRoles(
        window.userInfo?.kDomain_Id
      );
      const roles: IWorkspaceRole[] = aggregateWorkspaceRoles(response.data);
      this.setState({
        roleList: roles,
        filteredRoleList: this.getFilteredRoles(roles, this.state.searchText),
        isDataLoading: false
      });
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
      this.setState({ isDataLoading: false });
    }
  };

  getFilteredRoles = (roles: IWorkspaceRole[], searchText: string): IWorkspaceRole[] => {
    if (!searchText) {
      return roles ?? [];
    }
    const needle: string = searchText.toLowerCase();
    return (roles ?? []).filter((role: IWorkspaceRole): boolean => role.name.toLowerCase().indexOf(needle) > -1);
  };

  filterRoles = (event: ChangeEvent<HTMLInputElement>): void => {
    const searchFor: string = event.currentTarget.value;
    this.setState({
      searchText: searchFor,
      filteredRoleList: this.getFilteredRoles(this.state.roleList, searchFor)
    });
  };

  renderProjectsCell = (role: IWorkspaceRole): React.ReactNode => {
    const projectNames: string[] = role.occurrences
      .map((occurrence: IDomainWideProjectRole): string => occurrence.projectName)
      .filter((name: string, index: number, all: string[]): boolean => all.indexOf(name) === index);
    return (
      <div className="kuika-roles__used-in">
        {projectNames.map((name: string): React.ReactNode => (
          <KuikaBadge key={name} type="label" color="grey" variant="subtle" size="small" content={name} />
        ))}
      </div>
    );
  };

  getTableColumns = (): KuikaTableColumn<IWorkspaceRole>[] => {
    const columns: KuikaTableColumn<IWorkspaceRole>[] = [
      {
        id: "name",
        accessorKey: "name",
        enableSorting: true,
        width: 260,
        header: (): React.ReactNode => "Role",
        cell: (role: IWorkspaceRole): React.ReactNode => <KuikaRoleBadge role={role.name} />
      },
      {
        id: "projects",
        accessorFn: (role: IWorkspaceRole): number => role.projectCount,
        enableSorting: true,
        width: 420,
        header: (): React.ReactNode => "Used in",
        cell: (role: IWorkspaceRole): React.ReactNode => this.renderProjectsCell(role)
      }
    ];

    columns.push({
      id: "actions",
      enableSorting: false,
      width: 60,
      align: "right",
      header: (): React.ReactNode => <span />,
      cell: (role: IWorkspaceRole): React.ReactNode => (
        <div className="kuika-roles__row-actions">
          <KuikaDropdownMenu
            options={[
              {
                label: "Rename…",
                value: "rename",
                onClick: (): void => this.openRenameModal(role)
              },
              {
                label: "Manage projects…",
                value: "manage",
                onClick: (): void => this.openManageModal(role)
              }
            ]}
          >
            <KuikaButton type="tertiary" size="sm" isIcon data-testid="roleRowActionMenuButton">
              <KuikaSvg name="dots" width={16} height={16} pointer />
            </KuikaButton>
          </KuikaDropdownMenu>
        </div>
      )
    });

    return columns;
  };

  render(): React.ReactNode {
    return (
      <div className="kuika-settings-panel">
        <div className="kuika-settings-panel__header">
          <div className="kuika-settings-panel__header-left">
            <KuikaSvg width={24} height={24} name="shield-check" />
            <KuikaH4 data-testid="rolesPageTitle">Roles</KuikaH4>
          </div>
          <div className="kuika-settings-panel__header-right">
            <KuikaInput
              data-testid="searchRoleInput"
              onChange={this.filterRoles}
              value={this.state.searchText}
              placeholder="Search..."
            />
          </div>
        </div>
        <KuikaTable<IWorkspaceRole>
          data-testid="rolesTable"
          data={this.state.filteredRoleList}
          columns={this.getTableColumns()}
          rowKey={(row: IWorkspaceRole): string => row.name}
          pageSize={10}
          loading={this.state.isDataLoading}
          enableSorting
          emptyIcon={<KuikaSvg name="shield-check" width={48} height={48} />}
          emptyTitle="No roles yet"
          emptyDescription="Roles defined in your projects will appear here."
        />
        <ManageRoleProjectsModal
          role={this.state.manageRole}
          showModal={this.state.isManageModalOpen}
          closeModal={this.closeManageModal}
          onDone={this.loadRoles}
        />
        <RenameRoleModal
          role={this.state.renameRole}
          showModal={this.state.isRenameModalOpen}
          closeModal={this.closeRenameModal}
          onRenamed={this.loadRoles}
        />
      </div>
    );
  }
}

const Roles = withManagement(withHistory(RolesBase));
export { Roles };
